import useRequest from "@/lib/request/useRequest";
import { useI18n } from "@/lib/i18n/useI18n";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie } from "@fortawesome/free-solid-svg-icons";
import { Button, Chip } from "@heroui/react";
import { ListApplications, SectorSummary } from "@/types/operations";
import { roundTo } from "@/lib/utils/numbers";
import SummaryTable from "@/components/field/operations/SummaryTable";
import SectorTable from "@/components/field/operations/SectorTable";

function ApplicationSummary({
    fieldId,
    selectedCropCycle,
    dates,
}: {
    fieldId?: number;
    selectedCropCycle?: { start_date: string; end_date: string };
    dates?: { start?: string; end?: string };
}) {
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const { useGetRequest } = useRequest();
    const [viewSectorSummary, setViewSectorSummary] = useState(false);

    const applicationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: dates?.start,
            end_date: dates?.end,
        },
    });

    const sectorSummaryQuery = useGetRequest<SectorSummary[]>({
        url: `fields/${fieldId}/application_operations/sector_summary/`,
        enabled: !!fieldId && !!selectedCropCycle,
        params: {
            start_date: dates?.start,
            end_date: dates?.end,
        },
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const totalNApplied = useMemo(() => {
        return applicationOperationsQuery.data?.operations.reduce(
            (sum, operation) => sum + (operation.average_N || 0),
            0,
        );
    }, [applicationOperationsQuery.data?.operations]);

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <div className="font-semibold text-lg flex flex-row items-center gap-2">
                    <Button
                        isIconOnly
                        variant="solid"
                        color="primary"
                        aria-label="Sector Summary"
                        isLoading={sectorSummaryQuery.isLoading}
                        size="sm"
                        onPress={() => setViewSectorSummary(!viewSectorSummary)}
                    >
                        <FontAwesomeIcon icon={faChartPie} />
                    </Button>
                    {t("applications")}
                </div>
                {applicationOperationsQuery.data && (
                    <Chip color="success" variant="flat">
                        {t("total_n_applied")}:{" "}
                        {totalNApplied ? roundTo(totalNApplied, 1) : "0"}{" "}
                        {formatUnit("lb_ac", "kg_ha")}
                    </Chip>
                )}
            </div>
            {viewSectorSummary ? (
                <SectorTable sectorQuery={sectorSummaryQuery} />
            ) : (
                <SummaryTable operationsQuery={applicationOperationsQuery} />
            )}
        </>
    );
}

export default ApplicationSummary;
